import {
  getGetApiCertificationIdQueryKey,
  getGetApiCertificationProgramIdQueryKey,
  getGetApiCertificationProgramListQueryKey,
  getGetApiCertificationQueryKey,
  getGetApiDeviceIdQueryKey,
  getGetApiDeviceQueryKey,
  getGetApiGroupIdQueryKey,
  getGetApiGroupQueryKey,
  getGetApiInstitutionIdQueryKey,
  getGetApiInstitutionQueryKey,
  getGetApiInstitutionsInstitutionIdUsersUserIdRolesQueryKey,
  getGetApiInstitutionsInstitutionIdUsersUserIdRolesUserRoleIdPermissionsQueryKey,
  getGetApiInstitutionsInstitutionIdUsersUserIdRolesUserRoleIdScopesQueryKey,
  getGetApiLabInstitutionIdLabIdQueryKey,
  getGetApiLabQueryKey,
  getGetApiLogInstitutionIdQueryKey,
  getGetApiLogQueryKey,
  getGetApiRfidStatusQueryKey,
  getGetApiRfidTagsQueryKey,
  getGetApiRoleIdQueryKey,
  getGetApiRoleQueryKey,
  getGetApiServiceRequestsIdQueryKey,
  getGetApiServiceRequestsQueryKey,
  getGetApiStreamsQueryKey,
  getGetApiStreamsStreamIdQueryKey,
  getGetApiUserInstitutionIdUserIdQueryKey,
  getGetApiUserQueryKey,
  getGetReportRunQueryKey,
  getListReportRunsQueryKey,
} from '@smartsash/api-client';

import type { EntityName } from './entities';
import type { MutationCtx } from './invalidations';

// Query-key registry (03-03 §2.1): every invalidation and optimistic update
// references keys through THIS module, never the generated functions directly —
// so a spec regen that renames a key is a one-line fix here.

export const keys = {
  device: { list: getGetApiDeviceQueryKey, byId: getGetApiDeviceIdQueryKey },
  user: { list: getGetApiUserQueryKey, byKey: getGetApiUserInstitutionIdUserIdQueryKey },
  lab: { list: getGetApiLabQueryKey, byKey: getGetApiLabInstitutionIdLabIdQueryKey },
  group: { list: getGetApiGroupQueryKey, byId: getGetApiGroupIdQueryKey },
  institution: { list: getGetApiInstitutionQueryKey, byId: getGetApiInstitutionIdQueryKey },
  certification: { list: getGetApiCertificationQueryKey, byId: getGetApiCertificationIdQueryKey },
  certificationProgram: {
    list: getGetApiCertificationProgramListQueryKey,
    byId: getGetApiCertificationProgramIdQueryKey,
  },
  rfidTag: { list: getGetApiRfidTagsQueryKey, status: getGetApiRfidStatusQueryKey },
  stream: { list: getGetApiStreamsQueryKey, byId: getGetApiStreamsStreamIdQueryKey },
  role: { list: getGetApiRoleQueryKey, byId: getGetApiRoleIdQueryKey },
  // AssignmentController is retired; scopes and permissions no longer hang directly off `user`,
  // they hang off a specific `userRole` (a role assignment): `…/users/{userId}/roles/{userRoleId}/
  // …`. Prefix-based invalidation is unaffected — every one of them still lives under
  // `/api/institutions`.
  userRole: { byUser: getGetApiInstitutionsInstitutionIdUsersUserIdRolesQueryKey },
  scope: { byUserRole: getGetApiInstitutionsInstitutionIdUsersUserIdRolesUserRoleIdScopesQueryKey },
  permission: {
    byUserRole: getGetApiInstitutionsInstitutionIdUsersUserIdRolesUserRoleIdPermissionsQueryKey,
  },
  log: { list: getGetApiLogQueryKey, byInstitution: getGetApiLogInstitutionIdQueryKey },
  serviceRequest: {
    list: getGetApiServiceRequestsQueryKey,
    byId: getGetApiServiceRequestsIdQueryKey,
  },
  report: { runs: getListReportRunsQueryKey, run: getGetReportRunQueryKey },
} as const;

/**
 * The URL path prefix each entity's queries register under — the `[0]` of its
 * generated keys. Casing is inherited verbatim from the API and is inconsistent
 * (`/api/Device` vs `/api/rfid` vs `/api/tenants`), so these are copied from the
 * generated key bodies and pinned by a drift-guard test (`keys.test.ts`) that
 * fails if a regen moves them.
 */
export const entityRoot: Record<EntityName, string> = {
  device: '/api/Device',
  user: '/api/User',
  lab: '/api/Lab',
  group: '/api/Group',
  institution: '/api/Institution',
  certification: '/api/Certification',
  // Distinct from `/api/Certification` for invalidation purposes: `keyMatchesPrefix` compares
  // against `prefix` or `prefix + '/'`, and `/api/CertificationProgram` satisfies neither for the
  // shorter root, so the two resources never invalidate one another.
  certificationProgram: '/api/CertificationProgram',
  rfidTag: '/api/rfid',
  stream: '/api/streams',
  role: '/api/Role',
  userRole: '/api/institutions',
  scope: '/api/institutions',
  permission: '/api/institutions',
  log: '/api/Log',
  // The admin routes only. The portal's own `/api/portal/me/service-requests` reads live in the
  // other app and are invalidated there, by its hand-built option factories.
  serviceRequest: '/api/service-requests',
  report: '/api/reports',
};

/**
 * HS-1565: the role-assignment subtree of `/api/institutions` — a user's role grants and the
 * scopes and permissions hung off each one (`/api/institutions/{institutionId}/users/{userId}/
 * roles…`). Creating or updating a user grants or replaces its base role, so these reads go stale
 * outside the user's own parent chain. A pattern rather than an `entityRoot` prefix, so the
 * invalidation names exactly this subtree instead of the whole `/api/institutions` root. Pinned
 * against the generated keys by `keys.test.ts`.
 */
export const userRoleAssignmentPath = /^\/api\/institutions\/[^/]+\/users\/[^/]+\/roles\b/;

// Built from `entityRoot` rather than the generated `byId`/`byKey` functions: the entry chunk
// imports this module, and the generated functions would pull every one of them into the initial
// payload. `keys.test.ts` pins each path against its generated key.
const recordTrees: Partial<Record<EntityName, (ctx: MutationCtx) => string[]>> = {
  institution: ({ id }) => (id ? [`${entityRoot.institution}/${id}`] : []),
  group: ({ id }) => (id ? [`${entityRoot.group}/${id}`] : []),
  device: ({ id }) => (id ? [`${entityRoot.device}/${id}`] : []),
  lab: ({ institutionId, labId }) =>
    institutionId && labId ? [`${entityRoot.lab}/${institutionId}/${labId}`] : [],
  user: ({ institutionId, userId }) =>
    institutionId && userId
      ? [
          `${entityRoot.user}/${institutionId}/${userId}`,
          `${entityRoot.userRole}/${institutionId}/users/${userId}/roles`,
        ]
      : [],
};

/**
 * HS-1141: the key trees of ONE record — its own read and every path below it — named by the ids a
 * delete carries. A deleted record's cache is invalid by definition, so `useEntityDelete` skips
 * these trees when it invalidates instead of refetching a read that can only 404 (and be retried
 * three times). Only the entities an admin page reads by id; any other entity, or missing ids,
 * yields none. A user also owns its role-assignment subtree (HS-1565), gone with the user.
 */
export function recordTreesOf(entity: EntityName, ctx: MutationCtx): string[] {
  return recordTrees[entity]?.(ctx) ?? [];
}
